import React, { useEffect, useRef, useState } from "react";
import styles from "./CreateReportModal.module.css";
import { reactIcons } from "../../../icons/reactIcons";
import { homeApis } from "../../../apis/homeApis";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux-store/confirmSnackbarSlice";
import { reverseGeocode } from "../../../utils/reverseGeocode";

interface CreateReportModalProps {
  open: boolean;
  onClose: () => void;
  coords?: { lat: number; lng: number } | null;
  refreshReports?: () => void;
}

type ConcernValue = "Yes" | "No";
type ReportConcernKey = "illegalSmoking" | "zoningConcern" | "licenseConcern";
type ReportConcernSelection = ConcernValue | null;

interface SelectedImage {
  id: string;
  file: File;
  previewUrl: string;
}

const MAX_REPORT_IMAGES = 3;
const ALLOWED_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);
const ALLOWED_IMAGE_EXTENSIONS = /\.(jpe?g|png|webp)$/i;

const CreateReportModal: React.FC<CreateReportModalProps> = ({
  open,
  onClose,
  coords = null,
  refreshReports,
}) => {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
  const [showPickerPopup, setShowPickerPopup] = useState(false);
  const [reportConcerns, setReportConcerns] = useState<
    Record<ReportConcernKey, ReportConcernSelection>
  >({
    illegalSmoking: null,
    zoningConcern: null,
    licenseConcern: null,
  });

  const [showDesktopCamera, setShowDesktopCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [address, setAddress] = useState("");

  useEffect(() => {
    if (!coords) return;

    (async () => {
      const addr = await reverseGeocode(coords.lat, coords.lng);
      setAddress(addr);
    })();
  }, [coords]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("create-modal-open");
    } else {
      document.body.classList.remove("create-modal-open");
    }

    return () => document.body.classList.remove("create-modal-open");
  }, [open]);

  if (!open) return null;

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const allConcernsSelected = Object.values(reportConcerns).every(
    (value) => value !== null,
  );

  const isAllowedImageFile = (file: File) =>
    ALLOWED_IMAGE_MIME_TYPES.has(file.type) ||
    ALLOWED_IMAGE_EXTENSIONS.test(file.name);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const availableSlots = MAX_REPORT_IMAGES - selectedImages.length;

    if (availableSlots <= 0) {
      dispatch(
        showSnackbar({
          type: "error",
          message: "You can add maximum 3 images.",
          autoCloseDuration: 3000,
        }),
      );
      e.target.value = "";
      setShowPickerPopup(false);
      return;
    }

    const invalidFileCount = files.filter(
      (file) => !isAllowedImageFile(file),
    ).length;

    if (invalidFileCount > 0) {
      dispatch(
        showSnackbar({
          type: "error",
          message: "Only JPG, JPEG, PNG, and WEBP images are allowed.",
          autoCloseDuration: 3000,
        }),
      );
    }

    const validFiles = files.filter(isAllowedImageFile);

    if (validFiles.length) {
      const allowedFiles = validFiles.slice(0, availableSlots);
      const uploadedImages = allowedFiles.map((file, index) => ({
        id: `${Date.now()}-${index}-${file.name}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      setSelectedImages((prev) => [...prev, ...uploadedImages]);

      if (files.length > availableSlots) {
        dispatch(
          showSnackbar({
            type: "error",
            message: "Only 3 images can be added.",
            autoCloseDuration: 3000,
          }),
        );
      }
    }

    e.target.value = "";

    setShowPickerPopup(false);
  };

  const handleRemoveImage = (id: string) => {
    setSelectedImages((prev) => {
      const removedImage = prev.find((img) => img.id === id);
      if (removedImage) {
        URL.revokeObjectURL(removedImage.previewUrl);
      }

      return prev.filter((img) => img.id !== id);
    });
  };

  const handleConcernChange = (key: ReportConcernKey, value: ConcernValue) => {
    setReportConcerns((prev) => ({ ...prev, [key]: value }));
  };

  const resetReportForm = () => {
    selectedImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    setSelectedImages([]);
    setReportConcerns({
      illegalSmoking: null,
      zoningConcern: null,
      licenseConcern: null,
    });
    if (noteRef.current) noteRef.current.value = "";
  };

  const handleUploadClick = () => {
    if (selectedImages.length >= MAX_REPORT_IMAGES) {
      dispatch(
        showSnackbar({
          type: "error",
          message: "You can add maximum 3 images.",
          autoCloseDuration: 3000,
        }),
      );
      return;
    }

    setShowPickerPopup(true);
  };

  const openDesktopCamera = async () => {
    try {
      setShowDesktopCamera(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      alert("Camera access blocked or unavailable.");
    }
  };

  const capturePhoto = () => {
    if (selectedImages.length >= MAX_REPORT_IMAGES) {
      dispatch(
        showSnackbar({
          type: "error",
          message: "You can add maximum 3 images.",
          autoCloseDuration: 3000,
        }),
      );
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx?.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (!blob) return;

      const file = new File([blob], `report-photo-${Date.now()}.png`, {
        type: "image/png",
      });

      setSelectedImages((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${file.name}`,
          file,
          previewUrl: URL.createObjectURL(file),
        },
      ]);

      stopDesktopCamera();
      setShowDesktopCamera(false);

      if (cameraInputRef.current) cameraInputRef.current.value = "";
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }, "image/png");
  };

  const stopDesktopCamera = () => {
    const stream = videoRef.current?.srcObject as MediaStream;
    stream?.getTracks().forEach((t) => t.stop());
  };

  const submitReport = async () => {
    try {
      if (!coords) {
        dispatch(
          showSnackbar({
            type: "error",
            message: "Please confirm location first.",
            autoCloseDuration: 3000,
          }),
        );
        return;
      }

      if (!allConcernsSelected) return;

      setSubmitting(true);

      const payload = {
        reportDetails: {
          address: address,
          lat: coords.lat.toString(),
          long: coords.lng.toString(),
          notes: noteRef.current?.value || "",
          illegalSmoking: reportConcerns.illegalSmoking ?? "",
          zoningConcern: reportConcerns.zoningConcern ?? "",
          licenseConcern: reportConcerns.licenseConcern ?? "",
        },
        fileDetails: selectedImages.map((img) => ({
          filename: img.file.name,
          type: "IMAGE",
          description: img.file.name,
        })),
        files: selectedImages.map((img) => img.file),
      };

      await homeApis.submitReport(payload);

      dispatch(
        showSnackbar({
          type: "success",
          message: "Report submitted successfully!",
          autoCloseDuration: 3000,
        }),
      );

      onClose();
      refreshReports?.();

      resetReportForm();
    } catch (err) {
      dispatch(
        showSnackbar({
          type: "error",
          message: "Failed to submit report. Please try again.",
          autoCloseDuration: 3000,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>Create New Report</h3>
          <reactIcons.RxCrossIcon
            style={{ fontSize: "20px", fontWeight: "700", cursor: "pointer" }}
            onClick={() => {
              onClose();
              resetReportForm();
            }}
          />
        </div>

        <div className={styles.modalBody}>
          <div className={styles.formContent}>
            <div className={styles.section}>
              <label className={styles.label}>
                Images <span className={styles.required}>*</span>
              </label>

              <div
                className={`${styles.uploadBox} ${
                  selectedImages.length >= MAX_REPORT_IMAGES
                    ? styles.uploadBoxDisabled
                    : ""
                }`}
                onClick={handleUploadClick}
              >
                <reactIcons.IoCameraIcon fontSize={28} />
                <p>Upload Image</p>
                <span>{selectedImages.length}/3 images added</span>
              </div>

              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                capture="environment"
                ref={cameraInputRef}
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                multiple
                ref={galleryInputRef}
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />

              {selectedImages.length > 0 && (
                <div className={styles.previewGrid}>
                  {selectedImages.map((img, i) => (
                    <div key={img.id} className={styles.imagePreview}>
                      <img
                        src={img.previewUrl}
                        alt={`Preview ${i + 1}`}
                        className={styles.previewImg}
                      />
                      <button
                        type="button"
                        className={styles.removeBtn}
                        onClick={() => handleRemoveImage(img.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.section}>
              <label className={styles.label}>Note</label>
              <textarea
                ref={noteRef}
                className={styles.textarea}
                placeholder="Residential renovation without permit notice."
              ></textarea>
            </div>

            <div className={styles.section}>
              <label className={styles.label}>
                Concerns <span className={styles.required}>*</span>
              </label>
              <div className={styles.radioGroup}>
                <div className={styles.radioRow}>
                  <span className={styles.radioLabel}>Illegal Smoking</span>
                  <div className={styles.radioOptions}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name="illegalSmoking"
                        value="Yes"
                        checked={reportConcerns.illegalSmoking === "Yes"}
                        onChange={() =>
                          handleConcernChange("illegalSmoking", "Yes")
                        }
                      />
                      Yes
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name="illegalSmoking"
                        value="No"
                        checked={reportConcerns.illegalSmoking === "No"}
                        onChange={() =>
                          handleConcernChange("illegalSmoking", "No")
                        }
                      />
                      No
                    </label>
                  </div>
                </div>

                <div className={styles.radioRow}>
                  <span className={styles.radioLabel}>Zoning Concern</span>
                  <div className={styles.radioOptions}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name="zoningConcern"
                        value="Yes"
                        checked={reportConcerns.zoningConcern === "Yes"}
                        onChange={() =>
                          handleConcernChange("zoningConcern", "Yes")
                        }
                      />
                      Yes
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name="zoningConcern"
                        value="No"
                        checked={reportConcerns.zoningConcern === "No"}
                        onChange={() =>
                          handleConcernChange("zoningConcern", "No")
                        }
                      />
                      No
                    </label>
                  </div>
                </div>

                <div className={styles.radioRow}>
                  <span className={styles.radioLabel}>License Concern</span>
                  <div className={styles.radioOptions}>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name="licenseConcern"
                        value="Yes"
                        checked={reportConcerns.licenseConcern === "Yes"}
                        onChange={() =>
                          handleConcernChange("licenseConcern", "Yes")
                        }
                      />
                      Yes
                    </label>
                    <label className={styles.radioOption}>
                      <input
                        type="radio"
                        name="licenseConcern"
                        value="No"
                        checked={reportConcerns.licenseConcern === "No"}
                        onChange={() =>
                          handleConcernChange("licenseConcern", "No")
                        }
                      />
                      No
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <label className={styles.label}>Location Information</label>
              <div className={styles.locationBox}>
                <reactIcons.IoLocationOutlineIcon
                  fontSize={24}
                  color="#0155AE"
                />
                <div>
                  <p className={styles.address}>
                    {coords
                      ? address || "Fetching address..."
                      : "Error Fetching Address"}
                  </p>

                  <span className={styles.coords}>
                    {coords
                      ? `${coords.lat.toFixed(4)}° N, ${coords.lng.toFixed(4)}° W`
                      : "40.7484° N, 73.9857° W"}
                  </span>
                </div>
              </div>
              <p className={styles.locationNote}>
                📍 Location is automatically captured and cannot be edited
              </p>
            </div>
          </div>

          <div className={styles.actionBar}>
            <button
              className={styles.submitBtn}
              onClick={submitReport}
              disabled={
                submitting ||
                selectedImages.length === 0 ||
                !allConcernsSelected
              }
              type="button"
            >
              {submitting ? "Submitting..." : "Submit Report"}
            </button>
          </div>
        </div>
      </div>

      {showPickerPopup && (
        <div
          className={styles.popupOverlay}
          onClick={() => setShowPickerPopup(false)}
        >
          <div className={styles.popupBox} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.popupBtn}
              onClick={() => {
                if (isMobile) {
                  cameraInputRef.current?.click();
                } else {
                  openDesktopCamera();
                }
              }}
            >
              <reactIcons.IoCameraIcon fontSize={20} />
              Take a Photo
            </button>

            <button
              className={styles.popupBtn}
              onClick={() => galleryInputRef.current?.click()}
            >
              <reactIcons.IoImagesOutlineIcon fontSize={20} />
              Select from Gallery
            </button>

            <button
              className={styles.popupCancel}
              onClick={() => setShowPickerPopup(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showDesktopCamera && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            flexDirection: "column",
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            style={{
              width: "80%",
              maxWidth: "500px",
              background: "#000",
              borderRadius: "10px",
            }}
          ></video>

          <canvas ref={canvasRef} style={{ display: "none" }} />

          <button
            onClick={capturePhoto}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              fontSize: "16px",
              borderRadius: "8px",
              background: "#007bff",
              color: "#fff",
              border: "none",
            }}
          >
            Capture Photo
          </button>

          <button
            onClick={() => {
              stopDesktopCamera();
              setShowDesktopCamera(false);
            }}
            style={{
              marginTop: "10px",
              padding: "10px 20px",
              fontSize: "16px",
              borderRadius: "8px",
              background: "#ccc",
              color: "#000",
              border: "none",
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default CreateReportModal;
